import {conversationText, withConversationCopy, type ConversationDisplayCopy} from "@/lib/i18n/conversation-copy"
/**
 * Display labels for a conversation message's author, by who sent it.
 *
 * Single source of truth so a wording change (e.g. "Operator" → "Staff") is one
 * edit instead of a scattered find-and-replace. Used wherever a conversation is
 * flattened into human-readable text — handover transcripts and CRM session
 * notes today. PBX keeps its own "Operator" terminology; this is the
 * messaging/email conversation vocabulary only.
 */
export const CONVERSATION_AUTHOR_LABEL = {
  /** The end customer / contact. */
  customer: conversationText({key: 'authorCustomer'}),
  /** An AI-generated reply. */
  ai: conversationText({key: 'authorAi'}),
  /** A human teammate who replied (handover target). */
  staff: conversationText({key: 'authorStaff'}),
} as const

export type ConversationAuthorKind = keyof typeof CONVERSATION_AUTHOR_LABEL

/**
 * Who to name on a message. A colleague is named only from `sender_name` — never
 * from anything else, and never when the record holds no name.
 */
type AuthorInput = {
  role: ConversationAuthorKind
  contactTitle: string
  senderName?: unknown
}

export function resolveConversationAuthorLabel(input: AuthorInput): string {
  return resolveConversationAuthorDisplay(input).label
}

/** Named people remain customer data; only an unnamed role gets a translation descriptor. */
export function resolveConversationAuthorDisplay(input: AuthorInput): {label: string; copy?: ConversationDisplayCopy} {
  switch (input.role) {
    case "customer": return {label: input.contactTitle}
    case "ai": return withConversationCopy({}, {label: {key: "authorAi"}})
    case "staff": {
      const name = typeof input.senderName === "string" ? input.senderName.trim() : ""
      return name ? {label: name} : withConversationCopy({}, {label: {key: "authorStaff"}})
    }
  }
}
